/**
 * Which shared document an embed or a link in a page names, resolved against
 * the project's document index and never local disk paths. Pure, so the lazy
 * HTML page bundle can use it without the docs route.
 */
import { getCollabDocsSession, getSharedDocumentDisplayPath } from '@nimbalyst/collab-bundle/docs-ui';
import { parseCanvasDocumentUri } from '../canvas/canvasCardResolution';

export type DocsSnapshot = Awaited<ReturnType<NonNullable<ReturnType<typeof getCollabDocsSession>>['dataSource']['snapshot']>>;
type SnapshotItem = DocsSnapshot['items'][number];

const isResolvable = (item: SnapshotItem) => !item.decryptFailed && !item.trashedAt;
const displayPath = (snapshot: DocsSnapshot, item: SnapshotItem) => getSharedDocumentDisplayPath(item, snapshot.containers ?? []).replace(/^\.\//, '');

export function resolveDecisionArtifact(snapshot: DocsSnapshot, artifact: string, orgId: string, hostDocumentId: string): string | null {
  const uri = parseCanvasDocumentUri(artifact);
  if (uri && uri.orgId !== orgId) return null;
  const matches = snapshot.items.filter((item) => !item.decryptFailed && (uri
    ? item.documentId === uri.documentId
    : displayPath(snapshot, item) === artifact.replace(/^\.\//, '')));
  return matches.length === 1 && matches[0]!.documentId !== hostDocumentId ? matches[0]!.documentId : null;
}

/** `segments` resolved against `base`, `..` climbing out of it; `/` is the shared root. */
function joinPath(base: string, link: string): string {
  const parts = link.startsWith('/') ? [] : base.split('/').filter(Boolean);
  for (const segment of link.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') parts.pop();
    else parts.push(segment);
  }
  return parts.join('/');
}

/**
 * A page's embedded file link (`[Map](Product%20Map.excalidraw)`) resolved in
 * the project's shared documents, as the desktop resolves it: the path beside
 * the host page first, then a name only one document has. A page that is not
 * in the docs index (a typed page's body) resolves from the shared root.
 */
export function resolveEmbedFilePath(snapshot: DocsSnapshot, src: string, hostDocumentId: string): string | null {
  let link: string;
  try { link = decodeURIComponent(src.split(/[?#]/)[0]!); } catch { return null; }
  const candidates = snapshot.items.filter((item) => isResolvable(item) && item.documentId !== hostDocumentId);
  const host = snapshot.items.find((item) => item.documentId === hostDocumentId);
  const hostPath = host ? displayPath(snapshot, host) : '';
  const hostDir = hostPath.includes('/') ? hostPath.slice(0, hostPath.lastIndexOf('/')) : '';
  const target = joinPath(hostDir, link).toLowerCase();
  // The paths a document answers to: a markdown page made in the browser is
  // titled without `.md`, and a link to it names the file.
  const pathsOf = (item: SnapshotItem) => {
    const path = displayPath(snapshot, item).toLowerCase();
    return item.documentType === 'markdown' && !/\.(md|markdown)$/.test(path) ? [path, `${path}.md`] : [path];
  };
  const exact = candidates.filter((item) => pathsOf(item).includes(target));
  if (exact.length === 1) return exact[0]!.documentId;
  if (exact.length > 1) return null;
  const name = target.slice(target.lastIndexOf('/') + 1);
  const named = candidates.filter((item) => pathsOf(item).some((path) => path.slice(path.lastIndexOf('/') + 1) === name));
  return named.length === 1 ? named[0]!.documentId : null;
}
